import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { UiService } from '../../../core/services/ui.service';
import { StripeCheckoutService } from '../../../core/services/stripe-checkout.service';
import { OnboardingFlowService } from '../../../core/services/onboarding-flow.service';
import { OnboardingStepperComponent } from '../../../shared/components/onboarding-stepper/onboarding-stepper.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-checkout-subscription',
  standalone: true,
  templateUrl: './checkout-subscription.component.html',
  styleUrls: ['./checkout-subscription.component.scss'],
  imports: [CommonModule, BackButtonComponent, OnboardingStepperComponent],
})
export class CheckoutSubscriptionComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly uiService = inject(UiService);
  private readonly stripeCheckout = inject(StripeCheckoutService);
  private readonly onboardingFlow = inject(OnboardingFlowService);

  /** Show the registration progress bar only for TLL and only while mid-registration (not for existing
   * users buying later). Payment is always the 2nd of 3 steps here. */
  readonly showStepper = environment.brand === 'tll' && this.onboardingFlow.isPending();

  clientSecret: string | null = null;
  previousRoute = '/choose-memberships';
  flowLabel = 'Membership Options';
  errorMessage = '';
  successMessage = '';
  /** True until the Stripe embedded checkout has mounted (or failed) — nothing but a spinner shows
   * meanwhile, matching the account-creation step's own loading state (no half-rendered payment UI). */
  isCheckoutLoading = true;

  /** "Membership Options" back button is suppressed once payment is underway — going back from here
   * would silently abandon this Stripe session to browse other plans. "Account creation" stays (it is
   * the previous step of the same registration wizard, a legitimate way back). */
  get showBackButton(): boolean {
    return !this.isCheckoutLoading && this.flowLabel !== 'Membership Options';
  }

  async ngOnInit(): Promise<void> {
    const queryParams = this.route.snapshot.queryParamMap;
    this.clientSecret = queryParams.get('clientSecret');
    const saved = this.uiService.getPreviousRoute();

    if (saved.includes('/register')) {
      // Registration flow: the step before Payment is Account creation — the back button must be
      // named for it and return to that exact page (not the Membership options page).
      this.flowLabel = 'Account creation';
      this.previousRoute = saved;
    } else if (saved.includes('/pre-register')) {
      this.flowLabel = 'Membership Options';
      this.previousRoute = '/choose-memberships/pre-register';
    }

    if (!this.clientSecret) {
      this.router.navigate(['/dashboard/subscriptions']);
      return;
    }

    const result = await this.stripeCheckout.initEmbeddedCheckout(this.clientSecret, '#payment-element');
    if (!result.success) {
      this.errorMessage = result.message;
    }
    this.isCheckoutLoading = false;
  }

  ngOnDestroy(): void {
    this.stripeCheckout.destroy();
  }

  goBack(): void {
    this.router.navigateByUrl(this.previousRoute);
  }
}
